import { useNavigate, useParams } from "react-router-dom"
import {
  useGetDriverAuditLog,
  useGetDriverDetail
} from "../../services/queries"
import { Box, Grid, useMediaQuery, useTheme, Checkbox, FormControlLabel } from "@material-ui/core"
import NCText from "../../shared/NCText"
import TopNavBar from "../../components/TopNavBar"
import AddProfileIcon from "../../assets/icons/add_profile_image.svg"
import { GENDER, VEHICLE_FACILITIES } from "../../libs/config"
import FormSection from "../../components/FormSection"
import DriverDocuments from "../../components/driver/Document"
import NCButton from "../../shared/NCButton"
import NCLoader from "../../shared/NCLoader"
import AssignDispatcherModal from "../../components/driver/AssignDispatcherModal"
import { useRef, useState } from "react"
import { loggedInUser, saveQR } from "../../libs/utils"
import NCAuditButton from "../../shared/NCAuditButton"
import NotificationAlert from "../../components/notification/NotificationAlert"
import { QRCodeCanvas } from "qrcode.react"
import { useReactToPrint } from "react-to-print"
import { toast } from "react-toastify"
import { useQueryClient } from "react-query"
import { useAssignDispatcherToDriver, usePerformBackgroundCheck, useRejectDriver, useRequestAdditionalDocuments } from "../../services/mutations"
import { useTranslation } from "react-i18next"
import DriverInspectionList from "../../components/driver/DriverInspectionList"
import DriverRatingsList from "../../components/driver/DriverRatingsList"
import DriverScores from "../../components/driver/DriverScores"
import DriverTrainingList from "../../components/driver/DriverTrainingList"
import NCModal from "../../shared/NCModal"

const DriverDetail = () => {
  const queryClient = useQueryClient()
  const assignDispatcherApi = useAssignDispatcherToDriver()
  const rejectDriverApi = useRejectDriver()
  const [showBGCModel,setshowBGCModel] = useState(false);
  const { driverId } = useParams()
  const backgroundCheckAPI = usePerformBackgroundCheck();
  const requestAdditionalDocsAPI = useRequestAdditionalDocuments();
  const { t } = useTranslation()
  const [assignDispatcherModal, setAssignDispatcherModal] = useState(false)
  const [showRequestDocsModal, setShowRequestDocsModal] = useState(false)
  const [selectedDocIds, setSelectedDocIds] = useState([])
  const nav = useNavigate()
  const user = loggedInUser()
  const qrRef = useRef(null)
  const handlePrint = useReactToPrint({
    content: () => qrRef.current
  })
  const mobileTheme = useTheme()
  const isXSScreen = useMediaQuery(mobileTheme.breakpoints.down("xs"))
  const {
    isLoading,
    data: driverResult,
    refetch
  } = useGetDriverDetail(driverId)
  const driverData = driverResult?.data?.data || {}
  const { data } = useGetDriverAuditLog(
    {
      driver_id: driverId
    },
    { enabled: driverId ? true : false }
  )
  const auditData = data?.data?.data?.results || []

  const [hideBackgroundCheckButton, setHideBackgroundCheckButton] = useState(false) ;

  const isBGCDone = driverData?.background_check_status ? true : hideBackgroundCheckButton;

  const renderRowItem = (key, value) => {
    return (
      <>
      <Grid item md={4} xs={12} className="overflow-ellipsis">
        <NCText>
          <Box fontSize={"12px"} color={"#9CA3AF"}>
            {key}
          </Box>
        </NCText>
        <NCText className="overflow-ellipsis">{value ? value : "-"}</NCText>
      </Grid>
      </>
    )
  }

  const renderPrintButton = (
    <NCButton
      variant="contained"
      color="primary"
      disableElevation
      onClick={handlePrint}
      style={{ width: "auto", marginRight: "12px" }}
    >
      {t("common.print_qr")}
    </NCButton>
  )

  const isFacilityAvailable = facilityName => {
    if (!driverData?.vehicle?.vehicle_facilities) return false
    const facility = driverData?.vehicle?.vehicle_facilities.find(
      f => f.type === facilityName
    )
    return facility ? true : false
  }

  const getFacilityQuantityFromArray = facilityName => {
    if (!driverData?.vehicle?.vehicle_facilities) return "--"
    const facility = driverData?.vehicle?.vehicle_facilities.find(
      f => f.type === facilityName
    )
    return facility ? facility.quantity : "--"
  }

  const REQUIRED_DOCUMENTS_COUNT = 6

  const handleApproveOnDemandDriver = () => {
    assignDispatcherApi.mutate(
      { id: driverId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(["getDriverDetail"])
          toast.success(t("drivers.approved"))
          refetch?.()
        },
        onError: (error) => {
          toast.error(error?.response?.data || t("drivers.incomplete_documents", { count: REQUIRED_DOCUMENTS_COUNT }))
        }
      }
    )
  }

  const handleRejectDriver = () => {
    rejectDriverApi.mutate(driverId, {
      onSuccess: () => {
        queryClient.invalidateQueries(["getDriverDetail"])
        toast.success(t("drivers.rejected"))
        refetch?.()
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to reject driver")
      }
    })
  }

  const handleBackgroundCheck = () => {
    setHideBackgroundCheckButton(true)
    setshowBGCModel(false);
    backgroundCheckAPI.mutate(driverId,{
      onSuccess: () => {
        toast.success(t("drivers.background_check_complete_message"));
        setTimeout(()=>{
          refetch();
        },1000)
      },
      onError: () => {
        setHideBackgroundCheckButton(false);
      }
    })
  }

  const handleOpenRequestDocsModal = () => {
    setSelectedDocIds([])
    setShowRequestDocsModal(true)
  }

  const handleToggleDoc = (docId) => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    )
  }

  const handleRequestAdditionalDocuments = () => {
    if (selectedDocIds.length === 0) {
      toast.error(t("drivers.select_at_least_one_document"))
      return
    }
    requestAdditionalDocsAPI.mutate(
      { id: driverId, document_ids: selectedDocIds },
      {
        onSuccess: () => {
          toast.success(t("drivers.request_additional_documents_success"))
          setShowRequestDocsModal(false)
          queryClient.invalidateQueries(["getDriverDetail"])
          refetch()
        },
        onError: () => {
          toast.error("Failed to send request")
        }
      }
    )
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid
          container
          style={{ display: "flex", justifyContent: "space-between" }}
        >
          <Grid item md={4}>
            <Box mb={2} display={"flex"}>
              <NCText variant={"h4"} style={{ marginRight: "10px" }}>
                {t("drivers.driver_detail")}
              </NCText>
            </Box>
          </Grid>
          <Grid item container md={8} spacing={1} justifyContent="flex-end" alignItems="center" style={{ flexWrap: "wrap" }}>
          <Grid item>
            <Box>
              <NCButton
                  variant="contained"
                  color="primary"
                  onClick={handleOpenRequestDocsModal}
                >
                  {t("drivers.request_documents")}
                </NCButton>
              </Box>
            </Grid>
          {/* Only show once driver data has loaded, so the button doesn't
              flash during refresh before we know if a check exists */}
          {!isLoading && !isBGCDone && <Grid item>
            <Box>
              <NCButton
                  variant="contained"
                  color="primary"
                  onClick={()=> setshowBGCModel(true)}
                >
                  {t("drivers.run_background_check")}
                </NCButton>
              </Box>
            </Grid>}
            <Grid item>
              <Box>
                <NCButton
                  variant="contained"
                  color="primary"
                  onClick={() => nav(`/drivers/${driverId}/edit`)}
                >
                  {t("drivers.edit_driver")}
                </NCButton>
              </Box>
            </Grid>
            <Grid item>
              <Box display={"flex"}>
                {user.user_type === 1 && driverId ? (
                  <Box>
                    <NCAuditButton data={auditData} isLoading={isLoading} />
                  </Box>
                ) : null}
                <NotificationAlert />
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Box>
          <TopNavBar />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={4} md={2}>
            <Box display={"flex"} justifyContent={"center"}>
              <img
                src={driverData?.avatar || AddProfileIcon}
                width={"180px"}
                height="180px"
                style={{ borderRadius: driverData?.avatar ? "100%" : "0" }}
                htmlFor="contained-button-file"
              />
            </Box>
            <Grid container>
              <Grid item xs={12}>
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  marginTop={"20px"}
                  marginBottom={"20px"}
                  ref={qrRef}
                >
                  <QRCodeCanvas
                    id="qrcode-canvas"
                    style={{ height: "auto", width: "120px" }}
                    value={`${window.location.origin}/verify-driver/?uuid=${driverData?.uuid}`}
                    level="H"
                  />
                </Box>
                <Box
                  display={"flex"}
                  justifyContent={"center"}
                  marginTop={"20px"}
                  marginBottom={"20px"}
                >
                  <NCButton
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={saveQR.bind(this, qrRef)}
                    style={{ width: "auto", marginRight: "12px" }}
                  >
                    {t("common.save_qr")}
                  </NCButton>
                  {isXSScreen && renderPrintButton}
                </Box>
                {!isXSScreen && (
                  <Box
                    display={"flex"}
                    justifyContent={"center"}
                    marginTop={"10px"}
                    marginBottom={"10px"}
                  >
                    {renderPrintButton}
                  </Box>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} sm={8} md={10}>
            {[1, 2].includes(driverData?.driver_profile_status) ? (
              <Grid container className="success-container" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Grid item xs={12} md={8}>
                  <NCText>
                    {t("drivers.approved_message")}
                  </NCText>
                </Grid>
                <div style={{ display: "flex", gap: "12px" }}>
                  <NCButton
                    variant={"outlined"}
                    color={"secondary"}
                    onClick={handleRejectDriver}
                  >
                    {t("common.decline")}
                  </NCButton>
                  <NCButton
                    variant={"outlined"}
                    color={"primary"}
                    onClick={() =>
                      driverData?.driver_type === "Driver" && !driverData?.has_assigned_dispatcher
                        ? setAssignDispatcherModal(true)
                        : handleApproveOnDemandDriver()
                    }
                  >
                    {t("common.approve")}
                  </NCButton>
                </div>
              </Grid>
            ) : null}
            <FormSection title={t("drivers.driver_detail")}>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(t("common.first_name"), driverData?.first_name)}
                  {renderRowItem(t("common.middle_name"), driverData?.middle_name)}
                  {renderRowItem(t("common.last_name"), driverData?.last_name)}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(t("common.email_address"), driverData?.email)}
                  {renderRowItem(t("common.phone_number"), driverData?.phone)}
                  {renderRowItem(t("common.gender"), GENDER[driverData?.gender])}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(t("common.address"), driverData?.address)}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("job_country"),
                    driverData?.job_location_country
                  )}
                  {renderRowItem(
                    t("job_state"),
                    driverData?.job_location_state
                  )}
                  {renderRowItem(t("job_city"), driverData?.job_location_city)}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(t("list.driver_type"), driverData?.driver_type)}
                  {renderRowItem("Bank Connected", driverData?.stripe_connected ? "Yes" : "No")}
                  {renderRowItem("Assigned Dispatcher", driverData?.assigned_dispatcher_name || "-")}
                  {
                    isBGCDone ?
                      <>
                        {renderRowItem(t("drivers.background_check_status"), driverData?.background_check_status?.status)}
                        {renderRowItem(t("drivers.background_check_result"), driverData?.background_check_status?.result)}
                      </> : null
                  }
                </Grid>
              </Box>
            </FormSection>
            <FormSection title={t("vehicles.vehicle_details")}>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(t("list.mark"), driverData?.vehicle?.mark)}
                  {renderRowItem(t("list.model"), driverData?.vehicle?.model)}
                  {renderRowItem(t("list.vin"), driverData?.vehicle?.vin_no)}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("list.license_plate"),
                    driverData?.vehicle?.licence_plate
                  )}
                </Grid>
              </Box>
            </FormSection>
            <FormSection title={t("vehicles.vehicle_offering")}>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.buckle_guard"),
                    isFacilityAvailable(VEHICLE_FACILITIES.BUCKLE_GUARD)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.quantity"),
                    getFacilityQuantityFromArray(
                      VEHICLE_FACILITIES.BUCKLE_GUARD
                    )
                  )}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.booster"),
                    isFacilityAvailable(VEHICLE_FACILITIES.BOOSTER)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.quantity"),
                    getFacilityQuantityFromArray(VEHICLE_FACILITIES.BOOSTER)
                  )}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.safety_vest"),
                    isFacilityAvailable(VEHICLE_FACILITIES.SAFETY_VEST)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.quantity"),
                    getFacilityQuantityFromArray(VEHICLE_FACILITIES.SAFETY_VEST)
                  )}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.car_seat"),
                    isFacilityAvailable(VEHICLE_FACILITIES.CAR_SEAT)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.quantity"),
                    getFacilityQuantityFromArray(VEHICLE_FACILITIES.CAR_SEAT)
                  )}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.wheel_chair"),
                    isFacilityAvailable(VEHICLE_FACILITIES.WHEELCHAIR)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.service_dog"),
                    isFacilityAvailable(VEHICLE_FACILITIES.SERVICE_DOG)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                  {renderRowItem(
                    t("vehicles.travel_alone"),
                    isFacilityAvailable(VEHICLE_FACILITIES.TRAVEL_ALONE)
                      ? t("common.yes")
                      : t("common.no")
                  )}
                </Grid>
              </Box>
              <Box mb={2}>
                <Grid container>
                  {renderRowItem(
                    t("vehicles.vehicle_capacity"),
                    driverData?.vehicle?.capacity
                  )}
                </Grid>
              </Box>
            </FormSection>
            <FormSection title={t("drivers.all_ratings")}>
              <DriverRatingsList driverId={driverId} />
            </FormSection>
            <FormSection title={t("drivers.uploaded_documents")}>
              {driverData?.document ? (
                <DriverDocuments data={driverData?.document || []} />
              ) : null}
            </FormSection>
            <FormSection title={t("drivers.training_completed")}>
              <DriverTrainingList driverId={driverId} />
            </FormSection>
            <FormSection title={"Inspection"}>
                <DriverInspectionList driverId={driverId} />
            </FormSection>
            <FormSection title={t("drivers.scores")}>
              <DriverScores driverId={driverId} />
            </FormSection>
          </Grid>
        </Grid>
      </Grid>
      <AssignDispatcherModal
        driverId={driverId}
        refetch={refetch}
        modalState={assignDispatcherModal}
        handleClose={() => setAssignDispatcherModal(false)}
      />
      <NCModal
        open={showBGCModel}
        title={t("drivers.background_check")}
        text={t("drivers.bgc_message")}
        onClose={() => setshowBGCModel(false)}
        submitButton={
          <NCButton
            variant="contained"
            color="primary"
            disableElevation
            onClick={() => handleBackgroundCheck()}
            style={{ width: "auto" }}
          >
            {t("common.yes")}
          </NCButton>
        }
      />
      <NCModal
        open={showRequestDocsModal}
        title={t("drivers.request_additional_documents")}
        onClose={() => setShowRequestDocsModal(false)}
        submitButton={
          <NCButton
            variant="contained"
            color="primary"
            disableElevation
            disabled={requestAdditionalDocsAPI.isLoading}
            onClick={handleRequestAdditionalDocuments}
            style={{ width: "auto" }}
          >
            {t("common.submit")}
          </NCButton>
        }
      >
        <Box mt={2}>
          <NCText style={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
            {t("drivers.select_documents_to_request")}
          </NCText>
          {(driverData?.document || []).map(doc => (
            <Box key={doc.id}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedDocIds.includes(doc.id)}
                    onChange={() => handleToggleDoc(doc.id)}
                    color="primary"
                  />
                }
                label={doc.document_type_descr}
              />
            </Box>
          ))}
          {(!driverData?.document || driverData.document.length === 0) && (
            <NCText style={{ fontSize: "13px", color: "#9CA3AF" }}>
              {t("drivers.no_documents_uploaded")}
            </NCText>
          )}
        </Box>
      </NCModal>
      <NCLoader open={isLoading} />
    </Grid>
  )
}

export default DriverDetail
